import { ESCOPOS_PADRAO_SORT_FIELDS } from "@/modules/financeiro-configuracoes";
import type {
  OrdenarEscoposPadraoPor,
  VisaoEscopoPadrao,
} from "@/modules/financeiro-configuracoes";

export const ESCOPOS_PADRAO_TITULO = "Escopos macro padrão";
export const ESCOPOS_PADRAO_DESCRICAO =
  "Escopos macro que se repetem nas obras, com o custo de referência do ano. Eles aparecem ao criar o escopo da obra e servem de base para a projeção.";
export const ESCOPOS_PADRAO_LOADING_LABEL = "Carregando escopos macro padrão...";
export const ESCOPOS_PADRAO_ROWS_PER_PAGE_LABEL = "Escopos por página";
export const ESCOPOS_PADRAO_TABLE_LABEL = "Escopos macro padrão";
export const ESCOPOS_PADRAO_FORBIDDEN_MESSAGE =
  "Você não tem permissão para consultar os escopos macro padrão.";
export const ESCOPOS_PADRAO_ERROR_MESSAGE = "Não foi possível carregar os escopos macro padrão.";
export const ESCOPOS_PADRAO_VERSOES_ERROR_MESSAGE = "Não foi possível carregar as versões.";

export const ESCOPOS_PADRAO_VERSAO_ROTULO = "Versão";
export const ESCOPOS_PADRAO_VIGENTE_SUFIXO = "(vigente)";
export const ESCOPOS_PADRAO_VISOES_LABEL = "Visões dos escopos macro padrão";
export const ESCOPOS_PADRAO_MOSTRAR_INATIVOS_LABEL = "Mostrar inativos";
export const ESCOPOS_PADRAO_TAG_INATIVO = "Inativo";
export const ESCOPOS_PADRAO_NOVO_LABEL = "Novo escopo padrão";
export const ESCOPOS_PADRAO_NOVA_VERSAO_LABEL = "Criar nova versão";
export const ESCOPOS_PADRAO_NOVA_VERSAO_SEM_VERSAO_DICA =
  "Cadastre a primeira versão para criar uma nova a partir dela.";
export const ESCOPOS_PADRAO_SEM_VERSAO_TITULO = "Nenhuma versão cadastrada";
export const ESCOPOS_PADRAO_SEM_VERSAO_DESCRICAO =
  "Os escopos macro padrão aparecem aqui depois que a primeira versão for cadastrada.";
export const ESCOPOS_PADRAO_VERSAO_ANTERIOR_DICA = "Versão anterior, somente consulta";
export const ESCOPOS_PADRAO_VERSAO_ANTERIOR_AVISO = (vigente: string) =>
  `Versão anterior, somente consulta. A versão vigente é a ${vigente}.`;
/** Versão como o Figma escreve (`2027 · v01`); a API e o seletor guardam `2027 v01`. */
export const rotuloVersao = (versao: string): string => versao.replace(/ v(?=\d)/, " · v");

/**
 * Toasts de sucesso; nada de toast ao cancelar ou fechar. Os de criar escopo, atualizar custo, inativar e ativar vêm do
 * Figma. O de versão criada ainda está aberto com o design (o Figma 3.6 diz "Versão 2027 criada." e o 3.5 usa
 * "2027 · v01"): vale a redação com o sufixo e a chave consta em `ESCOPOS_PADRAO_TEXTOS_PROVISORIOS`.
 */
export const ESCOPOS_PADRAO_TOASTS = {
  escopoCriado: "Escopo macro criado com sucesso.",
  custoAtualizado: "Custo padrão atualizado.",
  versaoCriada: (versao: string) => `Versão ${rotuloVersao(versao)} criada.`,
  situacao: {
    inativar: (nome: string) => `Escopo padrão ${nome} inativado.`,
    ativar: (nome: string) => `Escopo padrão ${nome} ativado.`,
  },
} as const;

export const ESCOPOS_PADRAO_ATIVAR_ERRO = "Não foi possível ativar o escopo padrão.";

/** Mensagens de falha dos dados de apoio dos modais. */
export const ESCOPOS_PADRAO_ERROS = {
  equipamentos: "Não foi possível carregar os equipamentos.",
  equipamentosSemPermissao: "Você não tem permissão para consultar os equipamentos.",
  proximaVersao: "Não foi possível carregar a próxima versão.",
  proximaVersaoSemPermissao: "Você não tem permissão para criar uma nova versão.",
} as const;

/** Rótulos da correção da nova versão: redigidos sem fonte no Figma (ver `ESCOPOS_PADRAO_TEXTOS_PROVISORIOS`). */
export const ESCOPOS_PADRAO_ROTULOS = {
  percentualServicos: "Correção de Serviços (%)",
  percentualMateriais: "Correção de Materiais (%)",
} as const;

/** Chaves de `ESCOPOS_PADRAO_TOASTS`, `ESCOPOS_PADRAO_ERROS` e `ESCOPOS_PADRAO_ROTULOS` sem fonte firme, a fechar com o PO e o design. */
export const ESCOPOS_PADRAO_TEXTOS_PROVISORIOS = [
  "versaoCriada",
  "percentualServicos",
  "percentualMateriais",
] as const;

export const ESCOPOS_PADRAO_SEM_VALOR = "—";

/** Gatilho de cada modal; fica desabilitado até o modal chegar (situação e nova versão vêm depois). */
export const ESCOPOS_PADRAO_MODAIS_DISPONIVEIS = {
  novo: true,
  editar: true,
  situacao: true,
  novaVersao: true,
} as const;

export const ESCOPOS_PADRAO_FILTRO_TODOS = "";
export const ESCOPOS_PADRAO_FILTRO_TODOS_ROTULO = "Todos";
export const ESCOPOS_PADRAO_FILTRO_TIPO_OBRA_ROTULO = "Tipo de Obra";
export const ESCOPOS_PADRAO_FILTRO_GRUPO_ROTULO = "Grupo";
export const ESCOPOS_PADRAO_FILTRO_GRUPO_CLASSE_ROTULO = "Grupo de Classe";
export const ESCOPOS_PADRAO_FILTRO_EMBARCACAO_ROTULO = "Embarcação";

export const VISOES: readonly { valor: VisaoEscopoPadrao; rotulo: string }[] = [
  { valor: "EQUIPAMENTOS", rotulo: "Equipamentos" },
  { valor: "CLASSE", rotulo: "Classe" },
  { valor: "OUTROS", rotulo: "Outros" },
];

/** Códigos dos Tipos de Obra que cada visão atende. */
export const TIPOS_OBRA_POR_VISAO: Readonly<Record<VisaoEscopoPadrao, readonly string[]>> = {
  EQUIPAMENTOS: ["103"],
  CLASSE: ["101", "102"],
  OUTROS: ["100", "105", "301"],
};

/** Colunas que o core não ordena: ficam sem ordenação pelo cabeçalho. */
export type CelulaSemOrdem =
  | "fabricanteModelo"
  | "intermediariaHoras"
  | "intermediariaServicos"
  | "intermediariaMateriais"
  | "overhaulHoras"
  | "overhaulServicos"
  | "overhaulMateriais"
  | "servicos"
  | "materiais";

/** Uma célula por coluna; as ordenáveis têm o nome do campo de `sort_by` (a tag é `equipamento`). */
export type CelulaEscopoPadrao = OrdenarEscoposPadraoPor | CelulaSemOrdem;

export type EscoposPadraoColumnKey = CelulaEscopoPadrao | "acoes";

export const isCelulaOrdenavel = (key: CelulaEscopoPadrao): key is OrdenarEscoposPadraoPor =>
  ESCOPOS_PADRAO_SORT_FIELDS.some((campo) => campo === key);

export interface EscoposPadraoColuna {
  key: CelulaEscopoPadrao;
  header: string;
  /** Sem largura, a primeira coluna: a classe `colunaPrimeira` da tela dá 194 no desktop e 163 no mobile (Figma). */
  widthRem?: number;
  align?: "right";
}

const ALTURA_PX = 16;
const rem = (px: number) => px / ALTURA_PX;

// Colunas e larguras do Figma 13381:338310 (Equipamentos), 14334:491317 (Classe) e 14334:491578 (Outros). A primeira
// coluna é o nome do escopo com a última alteração embaixo (`ultimaAlteracao` deixou de ser coluna). O Figma agrupa
// Intermediária e Overhaul em dois níveis de cabeçalho; o HubTable não tem cabeçalho agrupado, então o grupo vai no
// texto de cada coluna.
export const COLUNAS_POR_VISAO: Readonly<
  Record<VisaoEscopoPadrao, readonly EscoposPadraoColuna[]>
> = {
  EQUIPAMENTOS: [
    { key: "equipamento", header: "Equipamento" },
    { key: "grupo", header: "Grupo", widthRem: rem(96) },
    { key: "embarcacao", header: "Embarcação", widthRem: rem(160) },
    { key: "fabricanteModelo", header: "Fabricante · Modelo", widthRem: rem(170) },
    {
      key: "intermediariaHoras",
      header: "Intermediária · Horas",
      widthRem: rem(96),
      align: "right",
    },
    {
      key: "intermediariaServicos",
      header: "Intermediária · Serviços",
      widthRem: rem(150),
      align: "right",
    },
    {
      key: "intermediariaMateriais",
      header: "Intermediária · Materiais",
      widthRem: rem(170),
      align: "right",
    },
    { key: "overhaulHoras", header: "Overhaul · Horas", widthRem: rem(96), align: "right" },
    { key: "overhaulServicos", header: "Overhaul · Serviços", widthRem: rem(150), align: "right" },
    {
      key: "overhaulMateriais",
      header: "Overhaul · Materiais",
      widthRem: rem(170),
      align: "right",
    },
  ],
  CLASSE: [
    { key: "escopo", header: "Escopo de classe" },
    { key: "grupo", header: "Grupo", widthRem: rem(112) },
    { key: "embarcacao", header: "Embarcação", widthRem: rem(160) },
    { key: "servicos", header: "Serviços", widthRem: rem(125), align: "right" },
    { key: "materiais", header: "Materiais", widthRem: rem(121), align: "right" },
  ],
  OUTROS: [
    { key: "escopo", header: "Escopo" },
    { key: "tipoObra", header: "Tipo de Obra", widthRem: rem(190) },
    { key: "embarcacao", header: "Embarcação", widthRem: rem(160) },
    { key: "servicos", header: "Serviços", widthRem: rem(121), align: "right" },
    { key: "materiais", header: "Materiais", widthRem: rem(125), align: "right" },
  ],
};

export const ESCOPOS_PADRAO_ACOES_KEY = "acoes";
export const ESCOPOS_PADRAO_ACOES_WIDTH_REM = rem(96);

/**
 * Colunas fixas à esquerda no desktop; no mobile só a primeira. Equipamentos: anotação do Figma 13390:342462 (Equipamento, Grupo e Embarcação).
 * Classe e Outros: o Figma (13381:338784 e 13381:339183) desenha a tabela inteira no card, sem rolagem nem anotação de coluna fixa; a primeira
 * coluna fixa vale para quando a janela estreita a faz rolar.
 */
export const COLUNAS_FIXAS_DESKTOP: Readonly<Record<VisaoEscopoPadrao, number>> = {
  EQUIPAMENTOS: 3,
  CLASSE: 1,
  OUTROS: 1,
};

/** Visões com mais colunas largas rolam na horizontal. */
export const TABLE_MIN_WIDTH_POR_VISAO: Readonly<Record<VisaoEscopoPadrao, string>> = {
  EQUIPAMENTOS: `${rem(1548)}rem`,
  CLASSE: `${rem(808)}rem`,
  OUTROS: `${rem(886)}rem`,
};
